import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const SuperAdminOrganizations = () => {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const { promoteSuperAdmin } = useAuth();
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteResult, setPromoteResult] = useState('');
  const [promoteError, setPromoteError] = useState('');

  const emptyForm = {
    name: '', slug: '', email: '', phone: '', website: '',
    address_line1: '', city: '', state: '', zip_code: '', country: 'US',
    is_active: true,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }
    fetchOrganizations();
  }, [isSuperAdmin]);

  const fetchOrganizations = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase?.from('organizations')?.select(`
          *,
          org_subscriptions(plan, status, max_users, current_user_count),
          org_members(id)
        `)?.order('created_at', { ascending: false });

      if (err) throw err;
      setOrganizations(data ?? []);
    } catch (err) {
      setError(err?.message ?? 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editOrg) {
        const { error: err } = await supabase?.from('organizations')?.update({
            name: form?.name, slug: form?.slug, email: form?.email,
            phone: form?.phone, website: form?.website,
            address_line1: form?.address_line1, city: form?.city,
            state: form?.state, zip_code: form?.zip_code, country: form?.country,
            is_active: form?.is_active,
          })?.eq('id', editOrg?.id);
        if (err) throw err;
      } else {
        const { data: newOrg, error: err } = await supabase?.from('organizations')?.insert({
            name: form?.name, slug: form?.slug, email: form?.email,
            phone: form?.phone, website: form?.website,
            address_line1: form?.address_line1, city: form?.city,
            state: form?.state, zip_code: form?.zip_code, country: form?.country,
            is_active: form?.is_active,
          })?.select()?.single();
        if (err) throw err;

        // Create default subscription for new org
        if (newOrg) {
          await supabase?.from('org_subscriptions')?.insert({
            organization_id: newOrg?.id,
            plan: 'starter',
            status: 'trialing',
            billing_email: form?.email,
            billing_name: form?.name,
          });
        }
      }
      setShowAddModal(false);
      setEditOrg(null);
      setForm(emptyForm);
      fetchOrganizations();
    } catch (err) {
      setError(err?.message ?? 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (org) => {
    try {
      await supabase?.from('organizations')?.update({ is_active: !org?.is_active })?.eq('id', org?.id);
      fetchOrganizations();
    } catch (err) {
      setError(err?.message);
    }
  };

  const openEdit = (org) => {
    setEditOrg(org);
    setForm({
      name: org?.name ?? '', slug: org?.slug ?? '', email: org?.email ?? '',
      phone: org?.phone ?? '', website: org?.website ?? '',
      address_line1: org?.address_line1 ?? '', city: org?.city ?? '',
      state: org?.state ?? '', zip_code: org?.zip_code ?? '',
      country: org?.country ?? 'US', is_active: org?.is_active ?? true,
    });
    setShowAddModal(true);
  };

  const filteredOrgs = organizations?.filter(org =>
    org?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    org?.slug?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    org?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const getStatusBadge = (sub) => {
    const status = sub?.[0]?.status ?? 'unknown';
    const colors = {
      active: 'bg-green-100 text-green-800',
      trialing: 'bg-blue-100 text-blue-800',
      past_due: 'bg-yellow-100 text-yellow-800',
      cancelled: 'bg-red-100 text-red-800',
      paused: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors?.[status] ?? 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const handlePromoteSuperAdmin = async () => {
    if (!promoteEmail?.trim()) return;
    setPromoteLoading(true);
    setPromoteResult('');
    setPromoteError('');
    try {
      const { data, error } = await promoteSuperAdmin(promoteEmail?.trim());
      if (error) {
        setPromoteError(error?.message || 'Failed to promote user.');
      } else {
        setPromoteResult(data || 'User promoted to superadmin successfully.');
        setPromoteEmail('');
      }
    } catch (err) {
      setPromoteError(err?.message || 'An error occurred.');
    } finally {
      setPromoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-6 px-4 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={18} color="#7c3aed" />
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Platform Organizations</h1>
            </div>
            <p className="text-muted-foreground text-sm">Superadmin: Manage all organizations on the platform</p>
          </div>
          <button
            onClick={() => { setEditOrg(null); setForm(emptyForm); setShowAddModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth"
          >
            <Icon name="Plus" size={16} color="currentColor" />
            Add Organization
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-xl p-4 shadow-elevation-1">
            <p className="text-sm text-muted-foreground">Total Orgs</p>
            <p className="text-2xl font-bold text-foreground">{organizations?.length}</p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-elevation-1">
            <p className="text-sm text-muted-foreground">Active</p>
            <p className="text-2xl font-bold text-green-600">{organizations?.filter(o => o?.is_active)?.length}</p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-elevation-1">
            <p className="text-sm text-muted-foreground">Trialing</p>
            <p className="text-2xl font-bold text-blue-600">
              {organizations?.filter(o => o?.org_subscriptions?.[0]?.status === 'trialing')?.length}
            </p>
          </div>
          <div className="bg-card rounded-xl p-4 shadow-elevation-1">
            <p className="text-sm text-muted-foreground">Total Members</p>
            <p className="text-2xl font-bold text-foreground">
              {organizations?.reduce((sum, o) => sum + (o?.org_members?.length ?? 0), 0)}
            </p>
          </div>
        </div>

        {/* Promote SuperAdmin Section */}
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={16} color="#7c3aed" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-purple-900">Grant Superadmin Access</h3>
              <p className="text-xs text-purple-700">Promote a user to superadmin with full access to all organizations</p>
            </div>
          </div>
          <div className="flex gap-3">
            <input
              type="email"
              placeholder="Enter user email address..."
              value={promoteEmail}
              onChange={e => setPromoteEmail(e?.target?.value)}
              onKeyDown={e => e?.key === 'Enter' && handlePromoteSuperAdmin()}
              className="flex-1 px-3 py-2 border border-purple-300 rounded-lg text-sm bg-white text-foreground focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            <button
              onClick={handlePromoteSuperAdmin}
              disabled={promoteLoading || !promoteEmail?.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-smooth disabled:opacity-50"
            >
              {promoteLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="ShieldCheck" size={15} color="white" />
              )}
              Promote
            </button>
          </div>
          {promoteResult && (
            <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <Icon name="CheckCircle" size={15} color="#16a34a" />
              {promoteResult}
            </div>
          )}
          {promoteError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <Icon name="AlertCircle" size={15} color="#dc2626" />
              {promoteError}
            </div>
          )}
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={e => setSearchTerm(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
        )}

        {/* Organizations Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="bg-card rounded-xl shadow-elevation-1 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Organization</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Plan</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Members</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Active</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrgs?.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-muted-foreground">
                        No organizations found
                      </td>
                    </tr>
                  ) : (
                    filteredOrgs?.map(org => (
                      <tr key={org?.id} className="border-b border-border hover:bg-muted/20 transition-smooth">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{org?.name}</div>
                          <div className="text-xs text-muted-foreground">{org?.slug}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-foreground">{org?.email ?? '—'}</div>
                          <div className="text-xs text-muted-foreground">{org?.phone ?? ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm capitalize text-foreground">
                            {org?.org_subscriptions?.[0]?.plan ?? 'starter'}
                          </span>
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(org?.org_subscriptions)}</td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground">{org?.org_members?.length ?? 0}</span>
                          <span className="text-xs text-muted-foreground"> / {org?.org_subscriptions?.[0]?.max_users ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleToggleActive(org)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              org?.is_active ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              org?.is_active ? 'translate-x-4' : 'translate-x-1'
                            }`} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => openEdit(org)}
                            className="p-1.5 rounded-lg hover:bg-muted transition-smooth text-muted-foreground hover:text-foreground"
                            title="Edit organization"
                          >
                            <Icon name="Pencil" size={15} color="currentColor" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevation-3 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-lg font-heading font-semibold text-foreground">
                {editOrg ? 'Edit Organization' : 'Add Organization'}
              </h2>
              <button
                onClick={() => { setShowAddModal(false); setEditOrg(null); setForm(emptyForm); }}
                className="p-1.5 rounded-lg hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={18} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">Organization Name *</label>
                  <input
                    required
                    value={form?.name}
                    onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="Acme Field Services"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Slug (URL) *</label>
                  <input
                    required
                    value={form?.slug}
                    onChange={e => setForm(f => ({ ...f, slug: e?.target?.value?.toLowerCase()?.replace(/[^a-z0-9-]/g, '-') }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="acme-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                  <input
                    type="email"
                    value={form?.email}
                    onChange={e => setForm(f => ({ ...f, email: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="admin@acme.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Phone</label>
                  <input
                    value={form?.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="(555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Website</label>
                  <input
                    value={form?.website}
                    onChange={e => setForm(f => ({ ...f, website: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="www.acme.com"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">Address</label>
                  <input
                    value={form?.address_line1}
                    onChange={e => setForm(f => ({ ...f, address_line1: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">City</label>
                  <input
                    value={form?.city}
                    onChange={e => setForm(f => ({ ...f, city: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">State</label>
                  <input
                    value={form?.state}
                    onChange={e => setForm(f => ({ ...f, state: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">ZIP</label>
                  <input
                    value={form?.zip_code}
                    onChange={e => setForm(f => ({ ...f, zip_code: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Country</label>
                  <input
                    value={form?.country}
                    onChange={e => setForm(f => ({ ...f, country: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={form?.is_active}
                    onChange={e => setForm(f => ({ ...f, is_active: e?.target?.checked }))}
                    className="w-4 h-4 rounded border-border"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-foreground">Active</label>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); setEditOrg(null); setForm(emptyForm); }}
                  className="flex-1 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editOrg ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminOrganizations;
